import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as reportProfilesApi from '../api/reportProfiles';
import * as reportRunsApi from '../api/reportRuns';

export function UtilityReports() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [profiles, setProfiles] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [profilesData, runsData] = await Promise.all([
        reportProfilesApi.getReportProfiles(selectedOrg.id, { 
          status: 'published',
          reportTypeId: 'report-type-utility-usage'
        }),
        reportRunsApi.getReportRuns(selectedOrg.id, {})
      ]);
      setProfiles(profilesData);
      setRecentRuns(runsData.slice(0, 5));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRunReport = (profileId) => {
    navigate(`/compliance/reports/${profileId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { bg: '#c6f6d5', color: '#276749' };
      case 'running': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'failed': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view utility reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Utility Reports"
        subtitle="Generate and manage utility usage reports"
      />

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Available Report Profiles</h3>
            <div style={styles.profilesGrid} data-testid="profiles-grid">
              {profiles.map(profile => (
                <div key={profile.id} style={styles.profileCard} data-testid={`profile-${profile.id}`}>
                  <h4 style={styles.profileName}>{profile.name}</h4>
                  <p style={styles.profileDesc}>{profile.description}</p>
                  <div style={styles.profileMeta}>
                    <span>Version: v{profile.currentVersion}</span>
                    <span>Program: {profile.programId}</span>
                  </div>
                  <button
                    onClick={() => handleRunReport(profile.id)}
                    style={styles.runButton}
                    data-testid={`run-${profile.id}`}
                  >
                    Run Report
                  </button>
                </div>
              ))}
              {profiles.length === 0 && (
                <div style={styles.emptyState}>
                  No published utility report profiles available.
                </div>
              )}
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Recent Runs</h3>
            <div style={styles.tableContainer}>
              <table style={styles.table} data-testid="recent-runs-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Profile</th>
                    <th style={styles.th}>Period</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Completed</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map(run => {
                    const statusStyle = getStatusColor(run.status);
                    return (
                      <tr key={run.id} style={styles.tr}>
                        <td style={styles.td}>{run.profileId}</td>
                        <td style={styles.td}>{run.periodStart} to {run.periodEnd}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            background: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {run.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {run.completedAt ? new Date(run.completedAt).toLocaleString() : '-'}
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => handleRunReport(run.profileId)}
                            style={styles.viewButton}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {recentRuns.length === 0 && (
                    <tr>
                      <td colSpan="5" style={styles.emptyState}>No recent runs</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Utility Report Features</h3>
            <div style={styles.featuresGrid}>
              <div style={styles.featureCard}>
                <h4 style={styles.featureTitle}>Monthly Usage by Site</h4>
                <p style={styles.featureDesc}>
                  Aggregated energy consumption data by site for utility program reporting.
                </p>
              </div>
              <div style={styles.featureCard}>
                <h4 style={styles.featureTitle}>Date Coverage Validation</h4>
                <p style={styles.featureDesc}>
                  Ensures all dates in the report period are covered with data.
                </p>
              </div>
              <div style={styles.featureCard}>
                <h4 style={styles.featureTitle}>Non-Negative kWh Check</h4>
                <p style={styles.featureDesc}>
                  Validates that all energy values are non-negative for data quality.
                </p>
              </div>
              <div style={styles.featureCard}>
                <h4 style={styles.featureTitle}>Configurable Templates</h4>
                <p style={styles.featureDesc}>
                  Use custom templates with schema maps for flexible output formats.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  section: { marginBottom: '2rem' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' },
  profilesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' },
  profileCard: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  profileName: { margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  profileDesc: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#718096' },
  profileMeta: { display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#a0aec0', marginBottom: '1rem' },
  runButton: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  statusBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  viewButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' },
  featureCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #3182ce' },
  featureTitle: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  featureDesc: { margin: 0, fontSize: '0.75rem', color: '#718096', lineHeight: '1.5' }
};
